import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SSIForm from './components/SSIForm';
import SSIReport from './components/SSIReport';
import { analyzeSsi, isSsiFailure } from './ssiApi';
import type { SSIFormData, SSIAnalysisState, SSIFormErrors } from './types';

function validate(data: SSIFormData): SSIFormErrors {
  const errors: SSIFormErrors = {};

  if (!data.cargo_atual.trim()) errors.cargo_atual = 'Cargo obrigatório';
  if (!data.area_interesse.trim()) errors.area_interesse = 'Áreas de interesse obrigatórias';
  if (!data.texto_sobre.trim()) errors.texto_sobre = 'Texto da seção Sobre obrigatório';

  const pilarErrors = data.pilares.map((p) => {
    if (p.score === '' || p.score === null || p.score === undefined) return 'Score obrigatório';
    const n = Number(p.score);
    if (isNaN(n) || n < 0 || n > 25) return 'Score deve ser entre 0 e 25';
    return '';
  });

  if (pilarErrors.some(Boolean)) errors.pilares = pilarErrors;

  return errors;
}

function hasErrors(errors: SSIFormErrors): boolean {
  if (errors.cargo_atual || errors.area_interesse || errors.texto_sobre) return true;
  if (errors.pilares?.some(Boolean)) return true;
  return false;
}

export default function SSIAnalysisPage() {
  const [status, setStatus] = useState<SSIAnalysisState>('idle');
  const [report, setReport] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<SSIFormErrors>({});

  async function handleSubmit(data: SSIFormData) {
    const errors = validate(data);
    if (hasErrors(errors)) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setErrorMessage('');
    setStatus('loading');

    const result = await analyzeSsi(data);

    if (isSsiFailure(result)) {
      setErrorMessage(result.message ?? 'Ocorreu um erro inesperado. Tente novamente.');
      setStatus('error');
      return;
    }

    setReport(result.report);
    setStatus('success');
  }

  return (
    <div className="font-inter">
      <Navbar />
      <main className="min-h-screen bg-zinc-50">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="mb-10">
            <span className="inline-block text-emerald-600 font-semibold text-sm font-inter tracking-widest uppercase mb-3">
              LinkedIn SSI
            </span>
            <h1 className="font-sora font-bold text-3xl md:text-4xl text-zinc-900 mb-4">
              Análise SSI
            </h1>
            <p className="font-inter text-zinc-500 text-base md:text-lg">
              Informe os dados do seu perfil LinkedIn e os scores dos 4 pilares SSI para receber um relatório personalizado com recomendações de melhoria.
            </p>
          </div>

          {status === 'error' && errorMessage && (
            <div
              role="alert"
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 font-inter text-sm"
            >
              {errorMessage}
            </div>
          )}

          <SSIForm onSubmit={handleSubmit} errors={fieldErrors} status={status} />

          {status === 'success' && <SSIReport report={report} />}
        </div>
      </main>
      <Footer />
    </div>
  );
}
